import { Date_toDateString, Date_toDateTimeString } from "../../../common/dateUtils";
import { FormState_PopulateErrorMessages } from "../../../common/formStateUtils";
import { String_removePrefix } from "../../../common/stringUtils";
import HullCleaningPermitRequest from "../../requests/HullCleaningPermitRequest";
import Errors from "../../responses/Errors";
import FormElement, { formElement } from "../FormElement";
import PersonFormElement, {
    PersonFormElement_default,
    PersonFormElement_toPerson,
    PersonFormElement_populateErrorMessages
} from "../PersonFormElement";
import PositionPickerRow, {
    PositionPickerRow_default,
    PositionPickerRow_toPosition,
    PositionPickerRows_populateErrorMessages
} from "./PositionPickerRow";

type HullCleaningPermitFormState = {
    remindMe: FormElement<boolean>;
    remindMeDate: FormElement<Date>;
    startDate: FormElement<Date>;
    endDate: FormElement<Date>;
    adminComment: FormElement<string>;
    description: FormElement<string>;
    positions: PositionPickerRow[];
    expirationReminderEmail: FormElement<string>;
    expirationReminderPhoneNumber: FormElement<string>;
    entrepreneur: FormElement<string>;
    shipName: FormElement<string>;
    callSign: FormElement<string>;
    shipRoute: FormElement<string>;
    colorSystem: FormElement<string>;
    cleaningMethod: FormElement<string>;
    methodIsApproved: FormElement<boolean>;
    contactPerson: PersonFormElement;
    anonymousUserEmail: FormElement<string>;
};

export default HullCleaningPermitFormState;

export function HullCleaningPermitFormState_default(): HullCleaningPermitFormState {
    const defaultFormState: HullCleaningPermitFormState = {
        adminComment: formElement(""),
        description: formElement(""),
        endDate: formElement(new Date()),
        entrepreneur: formElement(""),
        expirationReminderEmail: formElement(""),
        expirationReminderPhoneNumber: formElement(""),
        positions: [PositionPickerRow_default()],
        remindMe: formElement(false),
        remindMeDate: formElement(new Date()),
        startDate: formElement(new Date()),
        shipName: formElement(""),
        callSign: formElement(""),
        shipRoute: formElement(""),
        colorSystem: formElement(""),
        cleaningMethod: formElement(""),
        methodIsApproved: formElement(false),
        contactPerson: PersonFormElement_default(),
        anonymousUserEmail: formElement("")
    };

    return defaultFormState;
}

export function HullCleaningPermitFormState_populateErrorMessages(
    formState: HullCleaningPermitFormState,
    errors: Errors
): HullCleaningPermitFormState {
    const errorKeys = Object.keys(errors);

    const positionsErrorKeys = errorKeys.filter((e) => e.startsWith("Positions"));
    const positionErrors = Object.fromEntries(
        positionsErrorKeys.map((k) => [String_removePrefix(k, "Positions"), errors[k]])
    );

    const contactPersonErrorKeys = errorKeys.filter((e) => e.startsWith("ContactPerson."));
    const contactPersonErrors = Object.fromEntries(
        contactPersonErrorKeys.map((k) => [String_removePrefix(k, "ContactPerson."), errors[k]])
    );

    const updatedFormState = FormState_PopulateErrorMessages(formState, errors);
    updatedFormState.positions = PositionPickerRows_populateErrorMessages(formState.positions, positionErrors);

    updatedFormState.contactPerson = PersonFormElement_populateErrorMessages(
        formState.contactPerson,
        contactPersonErrors
    );

    return updatedFormState;
}

export function HullCleaningPermitFormState_toRequest(
    formState: HullCleaningPermitFormState
): HullCleaningPermitRequest {
    const request: HullCleaningPermitRequest = {
        adminComment: formState.adminComment.value,
        description: formState.description.value,
        endDate: Date_toDateTimeString(formState.endDate.value),
        entrepreneur: formState.entrepreneur.value,
        expirationReminderEmail: formState.expirationReminderEmail.value,
        expirationReminderPhoneNumber: formState.expirationReminderPhoneNumber.value,
        positions: formState.positions.map(PositionPickerRow_toPosition),
        remindMe: formState.remindMe.value,
        remindMeDate: Date_toDateString(formState.remindMeDate.value),
        startDate: Date_toDateTimeString(formState.startDate.value),
        shipName: formState.shipName.value,
        callSign: formState.callSign.value,
        cleaningMethod: formState.cleaningMethod.value,
        colorSystem: formState.colorSystem.value,
        contactPerson: PersonFormElement_toPerson(formState.contactPerson),
        methodIsApproved: formState.methodIsApproved.value,
        shipRoute: formState.shipRoute.value,
        anonymousUserEmail: formState.anonymousUserEmail.value,
    };

    return request;
}
